import { useCallback, useMemo, useState } from 'react';
import type { ShopifyOfferDto } from '../../../offer.types';
import { mergePartialCheckoutConfig } from '../../../services/checkoutConfig/checkoutConfig.merge';
import type { CheckoutConfig } from '../../../services/checkoutConfig/checkoutConfig.types';

/**
 * Resolve o `CheckoutConfig` efetivo do checkout:
 * - `baseConfig` explícito (modo preview, vindo de `GET /store-checkouts/preview/:nanoId`), ou
 * - `shopifyOffer.checkoutTheme.checkout.config` (fluxo normal — tema publicado da loja,
 *   sempre presente na resposta de `GET /checkout/offer/:uuid`).
 *
 * `applyPartialUpdate` aplica patches (merge raso por bloco) recebidos via `postMessage`
 * no modo preview, sem precisar de novo fetch.
 */
export function useCheckoutConfig(
  shopifyOffer: ShopifyOfferDto | null | undefined,
  baseConfig?: CheckoutConfig | null,
) {
  const resolvedBase = useMemo<CheckoutConfig | null>(() => {
    if (baseConfig) return baseConfig;
    if (!shopifyOffer) return null;
    return shopifyOffer.checkoutTheme.checkout.config;
  }, [baseConfig, shopifyOffer]);

  const [patch, setPatch] = useState<Partial<CheckoutConfig> | null>(null);

  const config = useMemo<CheckoutConfig | null>(() => {
    if (!resolvedBase) return null;
    if (!patch) return resolvedBase;
    return mergePartialCheckoutConfig(resolvedBase, patch);
  }, [resolvedBase, patch]);

  const applyPartialUpdate = useCallback((next: Partial<CheckoutConfig>) => {
    setPatch((prev) => (prev ? mergePartialCheckoutConfig(prev as CheckoutConfig, next) : next));
  }, []);

  return { config, applyPartialUpdate };
}
